import React from "react";
import { Package } from "lucide-react";

/**
 * ProductTile — a manufacturer-branded product hero card.
 *
 * When `image_url` is set on the product (post-scrape enrichment), we
 * render it with an on-error fallback to the branded placeholder. When
 * it's not, we generate a deterministic tile: manufacturer initial in a
 * colored circle, brand-colored background, product type label.
 *
 * Keeps the store visually rich without shipping any bundled imagery.
 * Deterministic — a given product renders identically across sessions.
 */

// Manufacturer → brand-accent color. Tunes the tile to the actual brand
// where we know it; falls back to a hash-based palette otherwise.
const BRAND_COLORS = {
  Samsung: "#1428A0",
  LG: "#A50034",
  Sony: "#000000",
  Panasonic: "#0F3E9A",
  Sharp: "#FF0000",
  Hisense: "#DA0027",
  TCL: "#00A9E0",
  Vestel: "#003C7E",
  ViewSonic: "#5A2D82",
  BenQ: "#8A00E5",
  NEC: "#003790",
  Philips: "#0C6EBE",
  Bosch: "#EA0016",
  Zebra: "#0072CE",
  Honeywell: "#DA291C",
  "Datalogic": "#003B7A",
  Intel: "#0068B5",
  Cisco: "#049FD9",
  Ubiquiti: "#0559C9",
  MikroTik: "#293896",
  Aruba: "#F6A800",
  Meraki: "#41B658",
  Legrand: "#000000",
  Schneider: "#3DCD58",
  Various: "#3F3F46",
};

const FALLBACK_PALETTE = [
  "#4C1D95", "#065F46", "#7C2D12", "#1E3A8A", "#831843",
  "#B45309", "#134E4A", "#4C0519", "#312E81", "#701A75",
];

function hashColor(seed) {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return FALLBACK_PALETTE[h % FALLBACK_PALETTE.length];
}

function brandColor(manufacturer) {
  if (!manufacturer) return "#3F3F46";
  return BRAND_COLORS[manufacturer] || hashColor(manufacturer);
}

export default function ProductTile({ p, size = "md" }) {
  const bg = brandColor(p?.manufacturer);
  const initial = ((p?.manufacturer || "?")[0] || "?").toUpperCase();
  const [errored, setErrored] = React.useState(false);
  const hasImage = p?.image_url && !errored;

  if (hasImage) {
    return (
      <div
        className="relative w-full h-full aspect-video bg-black rounded overflow-hidden border border-white/5"
        data-testid="product-tile-image"
      >
        <img
          src={p.image_url}
          alt={p.name}
          loading="lazy"
          onError={() => setErrored(true)}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  // Sizing knobs — sm for BOM rows, md for card grids, lg for detail pages.
  const initialSize =
    size === "lg" ? "text-6xl" : size === "sm" ? "text-2xl" : "text-4xl";
  const typeSize = size === "lg" ? "text-xs" : "text-[9px]";

  return (
    <div
      data-testid="product-tile-placeholder"
      className={`relative w-full h-full ${
        size === "sm" ? "aspect-square" : "aspect-video"
      } rounded overflow-hidden border border-white/5`}
      style={{
        backgroundImage: `linear-gradient(135deg, ${bg} 0%, ${bg}CC 60%, #000 200%)`,
      }}
    >
      {/* Corner mark — mimics our brand asset language */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.4) 0%, transparent 60%)",
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white/95">
        <div className={`${initialSize} font-black tracking-tight`}>
          {initial}
        </div>
        {size !== "sm" && (
          <div
            className={`${typeSize} font-mono uppercase tracking-[0.22em] text-white/70 mt-1 px-2 text-center line-clamp-1`}
          >
            {p?.product_type || p?.category || ""}
          </div>
        )}
      </div>
      {size === "sm" && (
        <div className="absolute bottom-1 right-1 text-white/40">
          <Package size={10} />
        </div>
      )}
    </div>
  );
}
